'use client';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  GetServerSideProps,
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
  NextPage,
} from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { BrandIcon } from '../../components/SelfListingV1/SelectBrand';
import { CardIcon } from '../../components/SelfListingV1/VerifyYourCar';
import BaseMobileCard from '../../components/SelfListingV1/components/BaseMobileCard';
import DetailsReference from '../../components/SelfListingV1/components/DetailsReference';
import { FadeIn } from '../../components/common/Animations';
import { FormInputV1 } from '../../components/common/Form';
import {
  ListingService,
  MasterdataService,
  VehicleService,
} from '../../helpers/services';
import AnalyticsService from '../../helpers/services/analytics.service';
import VasService from '../../helpers/services/vas.service';
import { useAppDispatch, useAppSelector } from '../../lib/hooks';
import {
  selfListingInitialState,
  setHasClickedVehicleDetailsForm,
  setLoader,
  setOpenSelfListingFlow,
  updateConfigurationData,
  updateContinueListingData,
  updateSelfListingFlow,
  updateSelfListingStep,
  updateUserDetails,
} from '../../lib/self-listing/selfListing.slice';
import { useTranslationContext } from '../../provider/TranslationProvider';
import { AppTheme } from '../../types/constants';
import {
  AnalyticsSource,
  ConfigurationKey,
  ListingStatus,
  Locales,
  VehicleListingSource,
} from '../../types/enums';
import { SelfListingConstants } from '../../types/i18n.labels';
import {
  Brand,
  ModelYearData,
  UpsertVehicleDetailsPayload,
} from '../../types/models';
import { translationsLang } from '../../utilities/constants';
import autoTranslate from '../../utilities/translations/autoTranslate';
import ConfigurationService from '../../helpers/services/configuration.service';
import IdentifierService from '../../helpers/services/identifier.service';
import { PushDataToGTM, PushSCEventsToGTM } from '../../helpers/utilities/gtm';
import {
  SellMyCarEvents,
  GTMEvents,
  PageNames,
  GTMSubEvents,
  PageCategories,
  EventLabels,
  LeadForms,
} from '../../types/gtm';
import {
  CommonUtils,
  SelfListingUtils,
  SessionUtils,
} from '../../helpers/utilities';
import ContinueListingMessagePopup from '../../components/componentsV2/selfListing/ContinueListingMessagePopup';
import { isMobile } from 'react-device-detect';
import { useCityContext } from '../../provider/CityProvider';
import { GreenTickMarkIcon } from '../../components/icons';
import { useSelector } from 'react-redux';
import { setFirstLoad } from '../../lib/global.slice';
import dynamic from 'next/dynamic';

const GogoLoader = dynamic(
  () => import('../../components/common/Loader/Loader')
);

type SelfListingPageProps = {
  title?: string;
  description?: string;
};
type IFormInput = {
  userId: string;
  sequenceNo: string;
};

const SelfListingPage: NextPage<SelfListingPageProps> = (
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) => {
  const isSelfListingFlowOpen = useAppSelector(
    ({ selfListing }) => selfListing.isOpen
  );
  const loader = useAppSelector(({ selfListing }) => selfListing.loader);
  const dispatch = useAppDispatch();

  const router = useRouter();
  const [modelYears, setModelYears] = useState<ModelYearData[]>([]);
  const selfListingSelector = useAppSelector(({ selfListing }) => selfListing);
  const [showCardReference, setShowCardReference] = useState<boolean>(false);
  const { selectCity } = useCityContext();
  const [popularBrands, setPopularBrands] = useState<Array<Brand>>([]);
  const currentFlowData = useAppSelector(({ selfListing }) => selfListing.data);

  useEffect(() => {
    MasterdataService.getPopularMakes(router.locale).then((resp) => {
      setPopularBrands(resp.success ? resp.data : []);
    });
  }, [router.locale]);

  // Effect to fetch and set max vehicle mileage
  useEffect(() => {
    Promise.all([
      ConfigurationService.fetchConfigurationValue(
        ConfigurationKey.MaxVehicleMileage
      ),
      ConfigurationService.fetchConfigurationValue(
        ConfigurationKey.MinAskingPrice
      ),
    ])
      .then(([maxMileageRes, MinPriceRes]) => {
        if (maxMileageRes && maxMileageRes.ConfigurationValue) {
          let maxVehicleMileage = parseInt(maxMileageRes.ConfigurationValue);
          dispatch(
            updateConfigurationData({ maxVehicleMileage: maxVehicleMileage })
          );
        }
        if (MinPriceRes && MinPriceRes.ConfigurationValue) {
          let minAskingPrice = parseInt(MinPriceRes.ConfigurationValue);
          dispatch(updateConfigurationData({ minAskingPrice: minAskingPrice }));
        }
      })
      .catch(console.error);
  }, [dispatch]);

  const { LabelConstants } = useTranslationContext();
  const pageVisited = useSelector((state: any) => state.global.isFirstLoad);
  useEffect(() => {
    const initialLoad = async () => {
      VehicleService.fetchModelYearAutoData().then((yearsData) => {
        setModelYears(
          yearsData.sort(
            (a, b) => parseInt(b.ModelYear) - parseInt(a.ModelYear)
          )
        );
      });
      if (selectCity?.CityKey && router.isReady) {
        PushDataToGTM(GTMEvents.DataLayerUpdate, {
          eventCategory: pageVisited
            ? GTMSubEvents.PageView
            : GTMSubEvents.PageRender,
          pageCategory: PageCategories.Sell_my_car,
          pageName: PageNames.Sell_my_car,
          pageTitle: PageNames.Sell_my_car,
          originalLocation: window?.location.href,
          source: isMobile ? 'MOBILE' : 'WEB',
          url: router.asPath,
          city: selectCity?.CityKey?.toUpperCase(),
        });
        dispatch(setFirstLoad(false));
      }
    };
    initialLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectCity, router.isReady]);

  useEffect(() => {
    const getContinueListingData = () => {
      const continueListingData =
        SelfListingUtils.getContinueListingDataFromCookies();
      const currentStep = SelfListingUtils.getContinueListingStepFromCookies();
      if (continueListingData && currentStep) {
        dispatch(updateSelfListingFlow(continueListingData));
        dispatch(
          updateContinueListingData({
            isVisible: false,
            data: {
              VehicleListingId: continueListingData.vehicle_listing_id,
              CurrentStep: currentStep,
            },
            isContinuingFromLink: true,
          })
        );
        dispatch(setOpenSelfListingFlow(true));
        SelfListingUtils.deleteContinueListingDataFromCookies();
        SelfListingUtils.deleteContinueListingStepFromCookies();
        if (SessionUtils.isValidSession()) {
          const user = SessionUtils.getUserDetails();
          if (String(continueListingData.sellerId) === String(user?.UserId)) {
            dispatch(updateSelfListingStep(currentStep));
          } else {
            dispatch(setOpenSelfListingFlow(false));
            dispatch(
              updateContinueListingData({
                showInvalidUserError: true,
              })
            );
            return;
          }
        } else {
          dispatch(
            updateSelfListingStep(
              SelfListingConstants.SelfListingAuthentication
            )
          );
        }
      }
    };
    getContinueListingData();
  }, []);

  const schema = yup.object({
    userId: yup
      .string()
      .min(
        8,
        LabelConstants.MIN_NUMBER_ERR_MSG?.replace(
          '{{name}}',
          LabelConstants.OWNER_ID
        )?.replace('{{length}}', '8')
      )
      .max(
        10,
        LabelConstants.MAX_NUMBER_ERR_MSG?.replace(
          '{{name}}',
          LabelConstants.OWNER_ID
        )?.replace('{{length}}', '10')
      )
      .typeError(LabelConstants.NUMBER_ERR_MSG)
      .required(LabelConstants.REQUIRED_FIELD),
    sequenceNo: yup
      .string()
      .min(
        9,
        LabelConstants.MIN_NUMBER_ERR_MSG?.replace(
          '{{name}}',
          LabelConstants.SEQUENCE_NUMBER
        )?.replace('{{length}}', '9')
      )
      .max(
        10,
        LabelConstants.MAX_NUMBER_ERR_MSG?.replace(
          '{{name}}',
          LabelConstants.SEQUENCE_NUMBER
        )?.replace('{{length}}', '10')
      )
      .typeError(LabelConstants.NUMBER_ERR_MSG)
      .required(LabelConstants.REQUIRED_FIELD),
  });

  const { control, handleSubmit } = useForm<IFormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      sequenceNo: selfListingSelector.user_details?.sequenceNo || '',
      userId: selfListingSelector.user_details?.userId || '',
    },
  });
  const getTranslatedText = async (text: string) => {
    const res = await autoTranslate(
      text,
      translationsLang.Arabic,
      translationsLang.English
    );
    return res[0]?.translations[0]?.text;
  };
  const handleGetDetails = async (data: IFormInput) => {
    let carDetails: any = null;
    let mappingData: any = null;
    try {
      dispatch(setLoader(true));
      const carDetailsData = await VasService.verifyCarDetails(data);
      if (![200, 201].includes(carDetailsData.status_code)) {
        dispatch(setLoader(false));
        const errMsg =
          carDetailsData?.result?.errorDetail?.errorMessage ||
          LabelConstants.SOMETHING_WENT_WRONG_ERROR;
        toast.error(errMsg, {
          position:
            router.locale === Locales.EN ? 'bottom-right' : 'bottom-left',
        });
        return;
      }
      carDetails = carDetailsData.result.vehicleInfo;
      mappingData = carDetailsData.result.mapping;
    } catch (err: any) {
      dispatch(setLoader(false));
      toast.error(err.message, {
        position: 'bottom-right',
      });
      return;
    }
    let selectedYear = modelYears.find(
      (item) => item.ModelYear === carDetails.modelYear
    );
    const previousBrandId = selfListingSelector.data.brand.brandId;
    const finalData = {
      brand: {
        brandId: mappingData?.make?.ggm_make_id || carDetails.makeCode,
        brandName: await getTranslatedText(carDetails.make),
      },
      model: {
        modelId: carDetails.modelCode,
        modelName: await getTranslatedText(carDetails.model),
        modelYear: carDetails.modelYear,
      },
      manufacture_year: {
        year: parseInt(selectedYear!.ModelYear),
        yearCode: selectedYear?.ModelYearCode,
      },
      plate_data: {
        plate1: await getTranslatedText(carDetails.plateText1),
        plate2: await getTranslatedText(carDetails.plateText2),
        plate3: await getTranslatedText(carDetails.plateText3),
        plate_number: carDetails.plateNumber,
      },
    };
    dispatch(updateSelfListingFlow(finalData));
    if (
      (mappingData?.make?.ggm_make_id || carDetails.makeCode) !==
      previousBrandId
    ) {
      dispatch(
        updateSelfListingFlow({ variant: { variantId: 0, variantName: '' } })
      );
    }
    dispatch(
      updateUserDetails({
        ...data,
      })
    );

    await AnalyticsService.createStrapiAnalytics(
      'sl_vehicle_registration_flow_selected',
      AnalyticsSource.SelfListing
    );

    const clarityId = await IdentifierService.getClarityUserId();
    PushDataToGTM(GTMEvents.DataLayerUpdate, {
      PageName: PageNames.Sell_my_car,
      pageCategory: PageCategories.Sell_my_car,
      eventCategory: LeadForms.Sell_car_lead,
      eventLabel: `${PageNames.Sell_my_car}>${EventLabels.REGISTRATION_FLOW_SELECTED}`,
      clarityId: clarityId,
      brand: finalData?.brand?.brandName?.toUpperCase?.(),
      model: finalData?.model?.modelName?.toUpperCase?.(),
      modelYear: String(finalData.manufacture_year.year),
    });
    PushSCEventsToGTM(SellMyCarEvents.REGISTRATION_FLOW_SELECTED, {
      clarity_id: clarityId,
      make: finalData.brand.brandName,
      model: finalData.model.modelName,
      model_year: String(finalData.manufacture_year.year),
    });

    dispatch(setLoader(false));

    dispatch(
      updateSelfListingStep(SelfListingConstants.SelfListingVehicleConfirmation)
    );
    dispatch(setOpenSelfListingFlow(true));
  };

  const handleBrandSelection = async (selectedBrand: Brand) => {
    const previousBrandId = selfListingSelector.data.brand.brandId;
    dispatch(
      updateSelfListingFlow({
        brand: {
          brandId: selectedBrand.VehicleMakeCode,
          brandName: selectedBrand.VehicleMake,
        },
      })
    );
    if (selectedBrand.VehicleMakeCode !== previousBrandId) {
      dispatch(
        updateSelfListingFlow({
          model: {
            modelId: 0,
            modelName: '',
            modelYear: 1990,
          },
          variant: { variantId: 0, variantName: '' },
          fuel_type: '',
          transmission: '',
          manufacture_year: {
            year: null,
            yearCode: -1,
          },
        })
      );
    }
    SelfListingUtils.saveSelfListingDataToCookies({
      ...selfListingSelector.data,
      brand: {
        brandId: selectedBrand.VehicleMakeCode,
        brandName: selectedBrand.VehicleMake,
      },
    });
    SelfListingUtils.setCurrentStepToCookies(
      SelfListingConstants.SelfListingSelectBrand
    );
    dispatch(setOpenSelfListingFlow(true));
    const clarityId = await IdentifierService.getClarityUserId();
    PushDataToGTM(GTMEvents.DataLayerUpdate, {
      eventCategory: LeadForms.Sell_car_lead,
      eventLabel: `${PageNames.Sell_my_car}>${EventLabels.BRAND_SELECTED}`,
      PageName: PageNames.Sell_my_car,
      pageCategory: PageCategories.Sell_my_car,
      brand: selectedBrand.VehicleMakeKey.toUpperCase(),
    });
    PushSCEventsToGTM(SellMyCarEvents.BRAND_CLICKED, {
      clarity_id: clarityId,
      make: selectedBrand.VehicleMake,
    });
    dispatch(
      updateSelfListingStep(
        SelfListingConstants.SelfListingSelectManufactureYear
      )
    );

    Promise.all([
      AnalyticsService.createStrapiAnalytics(
        'sl_brand_model_flow_selected',
        AnalyticsSource.SelfListing
      ),
      AnalyticsService.createStrapiAnalytics(
        'sl_brand_selected',
        AnalyticsSource.SelfListing
      ),
    ])
      .then(() => {})
      .catch(console.error);
  };

  useEffect(() => {
    async function sendAnalytics() {
      await AnalyticsService.createStrapiAnalytics(
        'self_listing_page_loaded',
        AnalyticsSource.SelfListing
      );
    }
    sendAnalytics();
  }, []);

  const handleContinueListing = () => {
    if (
      !selfListingSelector.continueListingData ||
      !selfListingSelector.continueListingData.fetchedData
    ) {
      return;
    }
    if (
      selfListingSelector.continueListingData.isFetched &&
      selfListingSelector.continueListingData.data
    ) {
      const nextStep = SelfListingUtils.getNextStepFromCurrentStep(
        selfListingSelector.continueListingData.data.CurrentStep
      );
      if (!nextStep) {
        return;
      }
      dispatch(updateSelfListingStep(nextStep));
      dispatch(setOpenSelfListingFlow(true));
      dispatch(
        updateSelfListingFlow(
          selfListingSelector.continueListingData.fetchedData
        )
      );
      dispatch(updateContinueListingData({ isVisible: false }));
    }
  };

  return (
    <>
      {/* <MetaDataComponent title={props.title} description={props.description} /> */}
      {showCardReference && (
        <div className="w-[90%] z-[101] sm:w-1/2">
          <DetailsReference closeTips={() => setShowCardReference(false)} />
        </div>
      )}

      {selfListingSelector.continueListingData.showInvalidUserError && (
        <ContinueListingMessagePopup status="invalidUser" />
      )}

      <div className="relative">
        {isSelfListingFlowOpen && <BaseMobileCard />}

        {loader && !isSelfListingFlowOpen && (
          <FadeIn className="h-full w-full z-50 flex justify-center items-center bg-black bg-opacity-10 fixed top-0 left-0 backdrop-blur-[2px]">
            <div style={{ aspectRatio: '1000/359' }} className="relative h-16">
              {/* <Image alt="" src={'/images/loader.gif'} layout="fill" /> */}
              <GogoLoader />
            </div>
          </FadeIn>
        )}
        <div className="relative w-full !z-0 aspect-[36/20] sm:aspect-[144/34]">
          <Image
            alt="Self Listing Hero Image"
            src={'/images/self-listing-hero-desktop.jpg'}
            objectFit="cover"
            layout="fill"
            priority={true}
            // width={1440}
            // height={340}
            className="!z-0 invisible sm:visible"
          />
          <Image
            alt={LabelConstants?.SELL_YOUR_CAR_EFFORTLESSLY}
            objectFit="cover"
            priority={true}
            // width={360}
            // height={200}
            layout="fill"
            src={'/images/self-listing-hero-mobile.jpg'}
            className="!z-0 visible sm:invisible"
          />
          <div className="absolute top-0 !z-20 left-0 w-full h-full bg-[linear-gradient(217.28deg,rgba(255,255,255,0)_0%,rgba(0,0,0,0)_35%,rgba(0,0,0,0.5)_100%)]">
            <div className="flex !z-20 items-center h-full mx-4 sm:mx-[146px] mb-[18px] lg:mb-[113px] justify-start rtl:justify-end">
              <h1
                className={`text-white !z-20 text-[26px] sm:text-[36px] md:text-[43px] w-[149px] sm:w-auto font-semibold transition-all`}
              >
                {LabelConstants.SELL_YOUR_CAR_EFFORTLESSLY}
              </h1>
            </div>
          </div>
        </div>
        <div className="relative pt-[calc(100vw/1.8px)] !z-10">
          <div className="mx-auto w-[312px] sm:w-[484px] ">
            <div className={`flex flex-col gap-[6px] mt-[19px]`}>
              {selfListingSelector.continueListingData.fetchedData && (
                <div className="px-[18px] sm:py-[24.44px] py-[16px] sm:px-[27.5px] -mt-[48px] sm:-mt-[72px] border border-[#E0E0E0] bg-white rounded-[12px] flex items-center justify-between gap-[10px]">
                  <div>
                    <h2 className="text-black text-13 sm:text-20 font-medium">
                      {SelfListingUtils.getVehicleNameFromStepAndData(
                        selfListingSelector.continueListingData.data
                          ?.CurrentStep,
                        selfListingSelector.continueListingData.fetchedData ||
                          selfListingSelector.data
                      )}
                    </h2>
                    {selfListingSelector.continueListingData.fetchedData.city
                      .cityName && (
                      <p className="text-11 sm:text-[16px] text-[#757575]">
                        {
                          selfListingSelector.continueListingData.fetchedData
                            .city.cityName
                        }
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleContinueListing}
                    className="py-[12px] sm:py-[18.33px] px-[16px] sm:px-[24.44px] bg-black text-white text-11 sm:text-[16px] rounded-full"
                  >
                    {LabelConstants?.CONTINUE}
                  </button>
                </div>
              )}

              {selfListingSelector.continueListingData.fetchedData ? (
                <p className="text-[18px] mt-[16px] font-medium text-[#212121] ">
                  {LabelConstants?.START_WITH_ANOTHER_CAR}
                </p>
              ) : (
                <p className="text-[18px] font-medium text-[#212121] ">
                  {LabelConstants?.SELECT_YOUR_CAR_BRAND}
                </p>
              )}

              <div className="mt-[6px] py-[16px] w-auto grid grid-cols-4 sm:grid-cols-5 gap-2 flex-1">
                {popularBrands.map((eachBrand, index) => (
                  <div
                    key={eachBrand.VehicleMakeCode}
                    onClick={() => handleBrandSelection(eachBrand)}
                  >
                    <BrandIcon
                      isSelected={
                        eachBrand.VehicleMakeCode ===
                        currentFlowData.brand.brandId
                      }
                      brand={eachBrand}
                    />
                  </div>
                ))}
              </div>
              <button
                onClick={async () => {
                  dispatch(
                    updateSelfListingStep(
                      SelfListingConstants.SelfListingSelectBrand
                    )
                  );
                  dispatch(setOpenSelfListingFlow(true));
                  IdentifierService.getClarityUserId().then((clarityId) => {
                    PushDataToGTM(GTMEvents.DataLayerUpdate, {
                      eventCategory: LeadForms.Sell_car_lead,
                      eventLabel: `${
                        PageNames.Sell_my_car
                      }>${EventLabels.VIEW_ALL_BRANDS_CLICKED.toUpperCase()}`,
                      clarityId: clarityId,
                    });
                    PushSCEventsToGTM(SellMyCarEvents.VIEW_ALL_BRANDS_CLICKED, {
                      clarity_id: clarityId,
                    });
                  });
                }}
                className="mt-[12px] mb-[30px] sm:mb-0 mx-auto text-[15px] sm:text-[18px] flex items-center font-medium text-primary"
              >
                {LabelConstants.VIEW_ALL_BRANDS}
                <svg
                  width="16"
                  height="17"
                  viewBox="0 0 16 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="rtl:rotate-180"
                >
                  <path
                    d="M6 12.5L10 8.5L6 4.5"
                    stroke="#4C0055"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div className="relative flex items-center w-full gap-4 py-4">
              <div className="h-px flex-1 bg-black/[4%]" />
              <span className="text-sm font-medium text-black/[30%]">OR</span>
              <div className="h-px flex-1 bg-black/[4%]" />
            </div>
            <div
              className={`w-full px-[18px] py-[17px] sm:px-[40px] sm:py-[30px] mx-auto transition-all overflow-hidden duration-300 rounded-[12px] bg-white shadow-[0px_1px_8px_0px_#0000001A]`}
            >
              <form
                className="space-y-[22px]"
                onClick={() => dispatch(setHasClickedVehicleDetailsForm(true))}
              >
                <FormInputV1
                  control={control}
                  name="userId"
                  placeholder="e.g. W1N0J8AB1MF29566"
                  label={
                    LabelConstants[
                      selfListingSelector.hasClickedVehicleDetailsForm
                        ? SelfListingConstants.OwnerId
                        : SelfListingConstants.StartWithOwnerId
                    ]
                  }
                  endIcon={
                    <CardIcon onClick={() => setShowCardReference(true)} />
                  }
                />
                {selfListingSelector.hasClickedVehicleDetailsForm && (
                  <>
                    <FormInputV1
                      control={control}
                      name="sequenceNo"
                      placeholder="e.g. W1N0J8AB1"
                      label={LabelConstants[SelfListingConstants.SequenceNo]}
                      endIcon={
                        <CardIcon onClick={() => setShowCardReference(true)} />
                      }
                    />
                    <button
                      className="bg-black -mt-[10px] text-white w-full h-[48px] sm:h-[60px] gap-1 rounded-[40px] font-semibold flex items-center justify-center text-[13px] sm:text-[20px]"
                      onClick={handleSubmit(handleGetDetails)}
                    >
                      {LabelConstants[SelfListingConstants.GetCarDetails]}
                    </button>
                  </>
                )}
              </form>
            </div>
          </div>
          {/* How it works */}
          <div className="bg-[#F8F8F8] flex my-[40px] py-[50px] flex-col gap-[40px] items-center">
            <p className="text-[28px] font-medium text-black ">
              {LabelConstants.HOW_IT_WORKS_V1}
            </p>
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-[100px] ">
              <div className="flex flex-col items-center gap-[20px]">
                <span className="w-[40px] flex items-center justify-center h-[40px] rounded-full bg-black text-white font-medium text-[20px]">
                  {LabelConstants.NUM_1}
                </span>
                <span className="text-[20px] w-[127px] text-center font-medium text-black ">
                  {LabelConstants.VEHICLE_DETAILS}
                </span>
              </div>
              <div className="flex flex-col items-center gap-[20px]">
                <span className="w-[40px] flex items-center justify-center h-[40px] rounded-full bg-black text-white font-medium text-[20px]">
                  {LabelConstants.NUM_2}
                </span>
                <span className="text-[20px] w-[184px] text-center font-medium text-black ">
                  {LabelConstants.ENTER_ADDITIONAL_INFORMATION}
                </span>
              </div>
              <div className="flex flex-col items-center gap-[20px]">
                <span className="w-[40px] flex items-center justify-center h-[40px] rounded-full bg-black text-white font-medium text-[20px]">
                  {LabelConstants.NUM_3}
                </span>
                <span className="text-[20px] w-[135px] text-center font-medium text-black ">
                  {LabelConstants.IMAGES}
                </span>
              </div>
              <div className="flex flex-col items-center gap-[20px]">
                <span className="w-[40px] flex items-center justify-center h-[40px] rounded-full bg-black text-white font-medium text-[20px]">
                  {LabelConstants.NUM_4}
                </span>
                <span className="text-[20px] w-[127px] text-center font-medium text-black ">
                  {LabelConstants.CONFIRM_DETAILS}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SelfListingPage;

export const getServerSideProps: GetServerSideProps = async ({
  locale,
}: GetServerSidePropsContext) => {
  const translations = await serverSideTranslations(locale || Locales.EN, [
    'common',
  ]);

  const title =
    translations._nextI18Next.initialI18nStore[locale ?? 'en']?.common
      ?.META_TITLE_SELL_FOR_ME;
  const description =
    translations._nextI18Next.initialI18nStore[locale ?? 'en']?.common
      ?.META_DESCRIPTION_SELL_FOR_ME;
  return {
    props: {
      ...translations,
      title,
      description,
      applyTheme: AppTheme.V1,
      imageUrl: `https://${process.env.NEXT_PUBLIC_APP_DOMAIN}/images/self-listing-hero-desktop.jpg`,
    },
  };
};
